(() => {
  "use strict";

  const config = window.TrisonWebsiteLeadCapture || {};
  if (!config.enabled || !config.endpoint) return;

  const routes = {
    TrisonRfqForm: "rfq",
    TrisonSampleForm: "sample",
    TrisonCallForm: "call",
  };

  const field = (formData, name) => String(formData.get(name) || "").trim();

  const normaliseDivision = (value) => {
    const text = String(value || "").toLowerCase();
    if (text.includes("food")) return "Food Ingredients";
    if (text.includes("packaging")) return "Sustainable Packaging & Products";
    if (text.includes("industrial") || text.includes("infrastructure") || text.includes("composite")) return "Industrial Materials";
    return value || "";
  };

  const createEventId = () => {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const getQueryValue = (name) => new URLSearchParams(window.location.search).get(name) || "";

  const setStatus = (form, message, state) => {
    let status = form.querySelector(".trison-web-lead-status");
    if (!status) {
      status = document.createElement("p");
      status.className = "trison-web-lead-status";
      status.setAttribute("role", "status");
      const grid = form.querySelector(".trison-b2b-form-grid");
      if (grid) grid.before(status);
      else form.prepend(status);
    }

    status.textContent = message;
    status.dataset.state = state;
  };

  const loadTurnstile = () =>
    new Promise((resolve, reject) => {
      if (window.turnstile) {
        resolve();
        return;
      }

      const existing = document.querySelector("script[data-trison-turnstile]");
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.trisonTurnstile = "true";
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener("error", reject, { once: true });
      document.head.appendChild(script);
    });

  const mountTurnstile = async (form) => {
    const siteKey = config.turnstileSiteKey || "";
    if (!siteKey || form.dataset.trisonTurnstileMounted === "true") return;

    const holder = document.createElement("div");
    holder.className = "trison-web-lead-turnstile";
    holder.dataset.trisonTurnstileHolder = "true";

    const button = form.querySelector('button[type="submit"]');
    if (button) button.before(holder);
    else form.appendChild(holder);

    try {
      await loadTurnstile();
      if (!window.turnstile) throw new Error("Turnstile unavailable");
      const widgetId = window.turnstile.render(holder, {
        sitekey: siteKey,
        theme: "light",
      });
      form.dataset.trisonTurnstileWidgetId = String(widgetId);
      form.dataset.trisonTurnstileMounted = "true";
    } catch {
      holder.remove();
      setStatus(
        form,
        "Security verification could not load. Please refresh the page before submitting your inquiry.",
        "error"
      );
    }
  };

  const buildPayload = (form, route) => {
    const data = new FormData(form);
    const productFromUrl = getQueryValue("product");
    const divisionFromUrl = getQueryValue("division");

    const base = {
      event_id: form.dataset.trisonEventId || createEventId(),
      route,
      contact_name: field(data, "contact[name]"),
      company_name: field(data, "contact[Company]"),
      email: field(data, "contact[email]"),
      country: field(data, "contact[Country]"),
      product_url: field(data, "contact[Product URL]") || getQueryValue("product_url"),
      page_url: window.location.href,
      utm_source: getQueryValue("utm_source"),
      utm_medium: getQueryValue("utm_medium"),
      utm_campaign: getQueryValue("utm_campaign"),
      utm_content: getQueryValue("utm_content"),
      utm_term: getQueryValue("utm_term"),
      website: field(data, "website"),
      turnstile_token: field(data, "cf-turnstile-response"),
    };

    form.dataset.trisonEventId = base.event_id;

    if (route === "rfq") {
      return {
        ...base,
        business_division: field(data, "contact[Product category]") || divisionFromUrl,
        inquiry_type: field(data, "contact[Inquiry type]"),
        product: field(data, "contact[Requested product]") || productFromUrl,
        product_specification: field(data, "contact[Product format]"),
        estimated_volume: field(data, "contact[Estimated volume]"),
        packaging_preference: field(data, "contact[Packaging preference]"),
        destination: field(data, "contact[Destination]"),
        target_timing: field(data, "contact[Target timing]"),
        requested_documents: field(data, "contact[Requested documents]"),
        details: field(data, "contact[body]"),
      };
    }

    if (route === "sample") {
      return {
        ...base,
        business_division: field(data, "contact[Product category]") || field(data, "contact[Division context]") || normaliseDivision(divisionFromUrl),
        inquiry_type: field(data, "contact[Information requested]"),
        product: field(data, "contact[Product / format]") || field(data, "contact[Requested product]") || productFromUrl,
        product_specification: field(data, "contact[Product / format]"),
        estimated_volume: field(data, "contact[Expected volume]"),
        destination: field(data, "contact[Sample destination]"),
        details: field(data, "contact[body]"),
      };
    }

    return {
      ...base,
      business_division: field(data, "contact[Topic]") || field(data, "contact[Division context]") || normaliseDivision(divisionFromUrl),
      inquiry_type: "Sourcing call request",
      product: field(data, "contact[Product or requirement]") || field(data, "contact[Requested product]") || productFromUrl,
      product_specification: field(data, "contact[Product or requirement]"),
      target_timing: field(data, "contact[Preferred time]"),
      details: field(data, "contact[body]"),
    };
  };

  const resetVerification = (form) => {
    if (window.turnstile && form.dataset.trisonTurnstileWidgetId) {
      window.turnstile.reset(form.dataset.trisonTurnstileWidgetId);
    }
  };

  const prefillContext = (form, route) => {
    const product = getQueryValue("product");
    const division = normaliseDivision(getQueryValue("division"));
    const productInput = form.querySelector("#sample-product");
    if (productInput && product && !productInput.value) productInput.value = product;

    const selectors = ["#rfq-category", "#sample-category", "#call-topic"];
    selectors.forEach((selector) => {
      const select = form.querySelector(selector);
      if (!select || !division) return;
      const option = Array.from(select.options).find((item) => item.value === division || item.text === division);
      if (option) select.value = option.value;
    });
  };

  const attach = (form, route) => {
    prefillContext(form, route);
    mountTurnstile(form);

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (form.dataset.trisonSubmitting === "true") return;

      const payload = buildPayload(form, route);
      if (config.turnstileSiteKey && !payload.turnstile_token) {
        setStatus(form, "Please complete the security verification before submitting.", "error");
        return;
      }

      form.dataset.trisonSubmitting = "true";
      const button = form.querySelector('button[type="submit"]');
      if (button) button.disabled = true;
      setStatus(form, "Submitting your B2B inquiry…", "loading");

      try {
        const response = await fetch(config.endpoint, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
        });

        let result = {};
        try {
          result = await response.json();
        } catch {
          result = {};
        }

        if (!response.ok || !result.ok) {
          throw new Error(result.error || "The inquiry could not be submitted.");
        }

        setStatus(
          form,
          result.message || "Thank you. Your inquiry has been received and will be reviewed by our B2B team.",
          "success"
        );
        form.reset();
        form.dataset.trisonEventId = "";
        resetVerification(form);
      } catch (error) {
        setStatus(
          form,
          error.message || "We could not submit your request right now. Please try again shortly.",
          "error"
        );
        resetVerification(form);
      } finally {
        form.dataset.trisonSubmitting = "false";
        if (button) button.disabled = false;
      }
    });
  };

  const initialise = () => {
    Object.entries(routes).forEach(([id, route]) => {
      const form = document.getElementById(id);
      if (form && form.dataset.trisonLeadBound !== "true") {
        form.dataset.trisonLeadBound = "true";
        attach(form, route);
      }
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialise, { once: true });
  } else {
    initialise();
  }
})();